import { useState, useEffect, useMemo, useCallback } from 'react';
import aviso, { textoDoErro } from '../lib/aviso';
import { Link } from 'react-router-dom';
import {
    LuPlus, LuSearch, LuPencil, LuEye, LuTrash2, LuLink, LuUnlink,
    LuUser, LuHandshake, LuChevronLeft, LuChevronRight, LuMail, LuExternalLink,
} from 'react-icons/lu';
import PageHeader from '../components/PageHeader';
import InfoTitulo from '../components/InfoTitulo';
import RecordPanel, { PanelSection } from '../components/RecordPanel';
import { PERMISSOES, temPermissao } from '../config/permissions';
import {
    ListRow, PerPage, RowAction, RowActions, ListCard, ListCards, FiltroPill,
} from '../components/ListKit';
import '../components/ListKit.css';
import { useCelular } from '../hooks/useCelular';
import { nomeEtapa } from '../data/etapas';
import './AssinaturasPage.css';
import {
    AssinaturaService, ROTULO_SITUACAO, SITUACAO, diasAteVencer, formatarData,
    type Assinatura, type AssinaturaPayload,
} from '../services/AssinaturaService';
import { ContactService, type Contact } from '../services/ContactService';
import { OpportunityService, type Opportunity } from '../services/OpportunityService';
import { ProductService, type Product } from '../services/ProductService';
import Select from '../components/Select';

const POR_PAGINA = [25, 50, 100];

/* A mesma string no cabeçalho e na linha — é o que mantém as colunas batendo. */
const COLUNAS = '1fr 1.2fr .8fr .7fr 1.1fr .6fr 78px';

type Modo = 'ver' | 'editar';
type Vinculo = 'contato' | 'negocio';

interface Rascunho {
    assinaturaClienteId?: number;
    identificador: string;
    nome: string;
    produtoId: string;
    situacao: number;
    expiraEm: string;
    aderiuEm: string;
    valor: string;
    quantidade: string;
    origem: string;
    observacao: string;
}

const VAZIO: Rascunho = {
    identificador: '', nome: '', produtoId: '', situacao: SITUACAO.ativa,
    expiraEm: '', aderiuEm: '', valor: '', quantidade: '1', origem: '', observacao: '',
};

/** ISO -> `yyyy-MM-dd`, que é o que o input[type=date] aceita. */
const paraCampoData = (iso?: string | null) => (iso ? iso.slice(0, 10) : '');

const paraRascunho = (a: Assinatura): Rascunho => ({
    assinaturaClienteId: a.assinaturaClienteId,
    identificador: a.identificador ?? '',
    nome: a.nome ?? '',
    produtoId: a.produtoId != null ? String(a.produtoId) : '',
    situacao: a.situacao,
    expiraEm: paraCampoData(a.expiraEm),
    aderiuEm: paraCampoData(a.aderiuEm),
    valor: a.valor != null ? String(a.valor) : '',
    quantidade: String(a.quantidade ?? 1),
    origem: a.origem ?? '',
    observacao: a.observacao ?? '',
});

/** Duas letras para o avatar do vínculo; o mesmo gesto de Contatos. */
const iniciais = (nome?: string | null) =>
    (nome ?? '').trim().split(/\s+/).slice(0, 2)
        .map(p => p[0]?.toUpperCase() ?? '').join('') || '?';

const moeda = (valor?: number | null) =>
    valor == null ? '—' : valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/**
 * As faixas de vencimento do filtro, ACUMULADAS de propósito: "7 dias" inclui
 * hoje, e "30 dias" inclui os 7. Quem cobra renovação pergunta "quem eu ligo
 * até sexta", não "quem cai exatamente na terceira semana" — então as
 * contagens das pills se sobrepõem, e isso é a leitura certa.
 *
 * Não há faixa acima de 30 dias: medido na base real, o ciclo é mensal e
 * nenhuma assinatura vence depois disso. Botão que sempre devolve zero é ruído.
 */
const FAIXAS: { rotulo: string; casa: (dias: number | null) => boolean }[] = [
    { rotulo: 'Venceu', casa: d => d !== null && d < 0 },
    { rotulo: 'Vence hoje', casa: d => d === 0 },
    { rotulo: 'Vence em 7 dias', casa: d => d !== null && d >= 0 && d <= 7 },
    { rotulo: 'Vence em 30 dias', casa: d => d !== null && d >= 0 && d <= 30 },
    { rotulo: 'Sem data', casa: d => d === null },
];

/** "em 12 dias" / "venceu há 3 dias" — o prazo relativo, que é o que decide a ação. */
const prazoRelativo = (dias: number): string => {
    if (dias === 0) return 'vence hoje';
    if (dias === 1) return 'vence amanhã';
    if (dias > 0) return `em ${dias} dias`;
    if (dias === -1) return 'venceu ontem';
    return `venceu há ${Math.abs(dias)} dias`;
};

/**
 * O texto do vencimento. É a única coluna que muda de cor, porque é a única
 * que pede ação: vencida é dívida, vence em até 7 dias é a semana de trabalho
 * de quem cobra renovação.
 */
const prazo = (a: Assinatura): { texto: string; tom: string } => {
    const dias = diasAteVencer(a.expiraEm);
    if (dias === null) return { texto: '—', tom: '' };

    const data = formatarData(a.expiraEm);
    /* Cancelada não tem prazo a cumprir: a data fica como registro, sem cor
       nem contagem — cobrar quem cancelou é o erro que a cor induziria. */
    if (a.situacao === SITUACAO.cancelada) return { texto: data, tom: '' };

    const texto = `${data} · ${prazoRelativo(dias)}`;
    if (dias < 0) return { texto, tom: 'asg-vencida' };
    if (dias <= 7) return { texto, tom: 'asg-vence-ja' };
    return { texto, tom: '' };
};

const AssinaturasPage = () => {
    const celular = useCelular();
    const podeGerenciar = temPermissao(PERMISSOES.assinaturasGerenciar);

    const [assinaturas, setAssinaturas] = useState<Assinatura[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [busca, setBusca] = useState('');
    const [situacao, setSituacao] = useState<string | null>(null);
    const [origem, setOrigem] = useState<string | null>(null);
    const [vencimento, setVencimento] = useState<string | null>(null);
    const [porPagina, setPorPagina] = useState(POR_PAGINA[0]);
    const [pagina, setPagina] = useState(1);

    const [aberta, setAberta] = useState<Assinatura | null>(null);
    const [modo, setModo] = useState<Modo>('ver');
    const [rascunho, setRascunho] = useState<Rascunho>(VAZIO);
    const [salvando, setSalvando] = useState(false);

    /* O seletor de vínculo carrega sob demanda: contato e oportunidade são as
       duas listas grandes do app, e a listagem não precisa delas para desenhar. */
    /* O catálogo vem junto com a listagem, não sob demanda: o Select do plano
       abre dentro do formulário, e esperar a lista com o painel já aberto
       mostraria um campo vazio que se preenche sozinho. */
    const [produtos, setProdutos] = useState<Product[]>([]);

    const [vinculando, setVinculando] = useState<Vinculo | null>(null);
    const [contatos, setContatos] = useState<Contact[]>([]);
    const [negocios, setNegocios] = useState<Opportunity[]>([]);
    const [buscaVinculo, setBuscaVinculo] = useState('');

    const carregar = useCallback(async () => {
        setIsLoading(true);
        try {
            const resposta = await AssinaturaService.getAll();
            setAssinaturas(resposta.dados ?? []);
        } catch (erro) {
            aviso.erro(textoDoErro(erro, 'Não foi possível carregar as assinaturas.'));
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { void carregar(); }, [carregar]);

    useEffect(() => {
        /* Empresa sem produto nenhum não é erro: a API responde 400 e o Select
           fica desabilitado com o aviso, em vez de a tela mostrar falha. */
        void ProductService.getAll()
            .then(r => setProdutos(r.dados ?? []))
            .catch(() => setProdutos([]));
    }, []);

    const opcoesProduto = useMemo(() => produtos.map(p => ({
        value: String(p.produtoId),
        label: p.nome,
        meta: p.preco ? moeda(p.preco) : undefined,
    })), [produtos]);

    /* Escolher o plano sugere o valor, mas só quando ainda não há um: o preço de
       tabela é ponto de partida, e sobrescrever um valor negociado à mão seria
       perder o que a pessoa digitou. */
    const escolherProduto = (valor: string) => {
        const produto = produtos.find(p => String(p.produtoId) === valor);
        setRascunho(r => ({
            ...r,
            produtoId: valor,
            valor: r.valor || (produto?.preco ? String(produto.preco) : r.valor),
        }));
    };

    const contar = (pegar: (a: Assinatura) => string) => {
        const m = new Map<string, number>();
        assinaturas.forEach(a => {
            const v = pegar(a);
            if (v) m.set(v, (m.get(v) ?? 0) + 1);
        });
        return [...m.entries()]
            .map(([valor, total]) => ({ valor, total }))
            .sort((a, b) => b.total - a.total || a.valor.localeCompare(b.valor, 'pt-BR'));
    };

    const opcoesSituacao = useMemo(
        () => contar(a => ROTULO_SITUACAO[a.situacao] ?? ''), [assinaturas]);
    const opcoesOrigem = useMemo(() => contar(a => a.origem ?? ''), [assinaturas]);

    /* A contagem responde antes do clique: "Venceu 59" já é a informação.
       Faixa vazia não vira botão — na base mensal, "sem data" costuma dar zero. */
    const opcoesVencimento = useMemo(() => FAIXAS
        /* O valor É o texto exibido: o FiltroPill do kit mostra `o.valor` na lista
           e na pill ligada, e ignora o campo `rotulo` da opção. */
        .map(f => ({
            valor: f.rotulo,
            total: assinaturas.filter(a => f.casa(diasAteVencer(a.expiraEm))).length,
        }))
        .filter(o => o.total > 0), [assinaturas]);

    const filtrados = useMemo(() => {
        const t = busca.trim().toLowerCase();
        return assinaturas.filter(a => {
            if (situacao && ROTULO_SITUACAO[a.situacao] !== situacao) return false;
            if (origem && a.origem !== origem) return false;
            if (vencimento) {
                const faixa = FAIXAS.find(f => f.rotulo === vencimento);
                if (faixa && !faixa.casa(diasAteVencer(a.expiraEm))) return false;
            }
            if (!t) return true;
            return [a.identificador, a.nome, a.produtoNome, a.origem, a.contatoNome]
                .some(campo => campo?.toLowerCase().includes(t));
        });
    }, [assinaturas, busca, situacao, origem, vencimento]);

    const totalPaginas = Math.max(1, Math.ceil(filtrados.length / porPagina));
    const paginaAtual = Math.min(pagina, totalPaginas);
    const inicio = (paginaAtual - 1) * porPagina;
    const visiveis = filtrados.slice(inicio, inicio + porPagina);

    /* Mudar filtro com a página 7 aberta deixaria a lista vazia sem motivo. */
    useEffect(() => { setPagina(1); }, [busca, situacao, origem, vencimento, porPagina]);

    const abrirVer = (a: Assinatura) => { setAberta(a); setModo('ver'); setRascunho(paraRascunho(a)); };

    const abrirNovo = () => {
        setAberta({ assinaturaClienteId: 0 } as Assinatura);
        setModo('editar');
        setRascunho(VAZIO);
    };

    const fechar = () => { setAberta(null); setVinculando(null); setBuscaVinculo(''); };

    const salvar = async () => {
        if (!rascunho.identificador.trim()) {
            aviso.erro('O identificador é obrigatório.');
            return;
        }
        setSalvando(true);
        try {
            const payload: AssinaturaPayload = {
                identificador: rascunho.identificador.trim(),
                nome: rascunho.nome.trim() || null,
                produtoId: rascunho.produtoId ? Number(rascunho.produtoId) : null,
                situacao: rascunho.situacao,
                expiraEm: rascunho.expiraEm || null,
                aderiuEm: rascunho.aderiuEm || null,
                valor: rascunho.valor ? Number(rascunho.valor) : null,
                quantidade: Number(rascunho.quantidade) || 1,
                origem: rascunho.origem.trim() || null,
                observacao: rascunho.observacao.trim() || null,
            };

            const resposta = rascunho.assinaturaClienteId
                ? await AssinaturaService.update(rascunho.assinaturaClienteId, payload)
                : await AssinaturaService.create(payload);

            aviso.ok(resposta.mensagem);
            await carregar();
            setAberta(resposta.dados);
            setModo('ver');
            setRascunho(paraRascunho(resposta.dados));
        } catch (erro) {
            aviso.erro(textoDoErro(erro, 'Não foi possível salvar a assinatura.'));
        } finally {
            setSalvando(false);
        }
    };

    const excluir = async (a: Assinatura) => {
        try {
            await AssinaturaService.remove(a.assinaturaClienteId);
            aviso.ok('Assinatura removida.');
            if (aberta?.assinaturaClienteId === a.assinaturaClienteId) fechar();
            await carregar();
        } catch (erro) {
            aviso.erro(textoDoErro(erro, 'Não foi possível remover a assinatura.'));
        }
    };

    const abrirVinculo = async (tipo: Vinculo) => {
        setVinculando(tipo);
        setBuscaVinculo('');
        try {
            if (tipo === 'contato' && contatos.length === 0) {
                const resposta = await ContactService.getAll();
                setContatos(resposta.dados ?? []);
            }
            if (tipo === 'negocio' && negocios.length === 0) {
                setNegocios(await OpportunityService.getAllOpportunities());
            }
        } catch (erro) {
            aviso.erro(textoDoErro(erro, 'Não foi possível carregar a lista.'));
        }
    };

    const gravarVinculo = async (tipo: Vinculo, id: number | null) => {
        if (!aberta) return;
        try {
            const resposta = tipo === 'contato'
                ? await AssinaturaService.vincularContato(aberta.assinaturaClienteId, id)
                : await AssinaturaService.vincularNegocio(aberta.assinaturaClienteId, id);
            aviso.ok(resposta.mensagem);
            setAberta(resposta.dados);
            setVinculando(null);
            setBuscaVinculo('');
            await carregar();
        } catch (erro) {
            aviso.erro(textoDoErro(erro, 'Não foi possível gravar o vínculo.'));
        }
    };

    const opcoesVinculo = useMemo(() => {
        const termo = buscaVinculo.trim().toLowerCase();
        if (vinculando === 'contato') {
            return contatos
                .filter(c => !termo || c.nome?.toLowerCase().includes(termo) || c.telefone?.includes(termo))
                .slice(0, 50)
                .map(c => ({ id: c.contatoId, titulo: c.nome || 'Sem nome', apoio: c.telefone || '' }));
        }
        if (vinculando === 'negocio') {
            return negocios
                .filter(n => !termo || n.nomeContato?.toLowerCase().includes(termo))
                .slice(0, 50)
                .map(n => ({
                    id: n.idConversa,
                    titulo: n.nomeContato || 'Sem nome',
                    apoio: `${n.descricaoStatus} · ${moeda(n.valor)}`,
                }));
        }
        return [];
    }, [vinculando, contatos, negocios, buscaVinculo]);

    const limparFiltros = () => { setBusca(''); setSituacao(null); setOrigem(null); setVencimento(null); };

    const listaVazia = (
        <div className="lk-blank">
            <div className="lk-blank-title">
                {assinaturas.length === 0 ? 'Nenhuma assinatura cadastrada' : 'Nada com esses filtros'}
            </div>
            <p className="lk-blank-text">
                {assinaturas.length === 0
                    ? (podeGerenciar
                        ? 'Cadastre os assinantes da sua operação para acompanhar vencimento e renovação em um lugar só.'
                        : 'Os assinantes da sua operação aparecem aqui.')
                    : 'Ajuste a busca ou limpe os filtros para ver os outros registros.'}
            </p>
            {assinaturas.length === 0
                ? (podeGerenciar && (
                    <button type="button" className="lk-btn is-primary" onClick={abrirNovo}>
                        <LuPlus size={14} /> Nova assinatura
                    </button>
                ))
                : (
                    <button type="button" className="lk-btn" onClick={limparFiltros}>
                        Limpar filtros
                    </button>
                )}
        </div>
    );

    const rodape = (
        <div className="lk-foot">
            <PerPage value={porPagina} options={POR_PAGINA} onChange={setPorPagina} />

            <span className="lk-range">
                {inicio + 1}–{Math.min(inicio + porPagina, filtrados.length)} de {filtrados.length}
            </span>

            <span className="lk-pages">
                <button type="button" className="lk-page" aria-label="Página anterior"
                    disabled={paginaAtual === 1} onClick={() => setPagina(p => Math.max(1, p - 1))}>
                    <LuChevronLeft size={13} />
                </button>
                <span className="lk-page is-current">{paginaAtual}</span>
                <span className="lk-muted">de {totalPaginas}</span>
                <button type="button" className="lk-page" aria-label="Próxima página"
                    disabled={paginaAtual === totalPaginas} onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))}>
                    <LuChevronRight size={13} />
                </button>
            </span>
        </div>
    );

    return (
        <div className="page-shell lk-screen">
            <PageHeader
                title="Assinaturas"
                subtitle="Quem paga a sua operação todo mês"
                titleInfo={(
                    <InfoTitulo rotulo="O que são Assinaturas">
                        <span>
                            Os assinantes recorrentes da sua operação, com vencimento e situação.
                        </span>
                        <span>
                            O assinante não é um contato: ele é identificado pelo login no seu
                            serviço. Quando houver um contato ou um negócio correspondente, o
                            vínculo é feito à mão, no painel de cada assinatura.
                        </span>
                    </InfoTitulo>
                )}
                /* Lista vazia: a porta é a do aviso, no meio da tela. */
                actions={!isLoading && assinaturas.length === 0 ? undefined : (
                    <div className="lk-actions">
                        {podeGerenciar && (
                            <button type="button" className="lk-btn is-primary" onClick={abrirNovo}>
                                <LuPlus size={14} /> Nova assinatura
                            </button>
                        )}
                    </div>
                )}
            />

            <div className="page-body lk-fill">
                <div className="lk-filters">
                    <label className="lk-search">
                        <LuSearch size={14} />
                        <input
                            type="search"
                            placeholder="Buscar por identificador, nome, plano ou origem…"
                            value={busca}
                            onChange={e => setBusca(e.target.value)}
                        />
                    </label>

                    {/* Vencimento vem primeiro: é o filtro que responde "o que eu faço
                        hoje". Situação é o que alguém digitou; vencimento é o calendário. */}
                    <FiltroPill rotulo="Vencimento" valor={vencimento} opcoes={opcoesVencimento} onChange={setVencimento} />
                    <FiltroPill rotulo="Situação" valor={situacao} opcoes={opcoesSituacao} onChange={setSituacao} />
                    <FiltroPill rotulo="Origem" valor={origem} opcoes={opcoesOrigem} onChange={setOrigem} />

                    <span className="lk-count">
                        {assinaturas.length} {assinaturas.length === 1 ? 'assinatura' : 'assinaturas'}
                        {filtrados.length !== assinaturas.length && ` · ${filtrados.length} no filtro`}
                    </span>
                </div>

                {/* Celular: a grade não cabe em 358, então a linha vira cartão —
                    identificador, situação e vencimento, que é o que decide ação. */}
                {celular ? (
                    <>
                        <ListCards>
                            {isLoading
                                ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : visiveis.map(a => (
                                    <ListCard
                                        key={a.assinaturaClienteId}
                                        titulo={a.identificador}
                                        apoio={a.nome || 'sem nome'}
                                        estado={
                                            <>
                                                <span className={`asg-selo asg-selo-${a.situacao}`}>
                                                    {ROTULO_SITUACAO[a.situacao]}
                                                </span>
                                                <span className={prazo(a).tom}>· {prazo(a).texto}</span>
                                            </>
                                        }
                                        selected={aberta?.assinaturaClienteId === a.assinaturaClienteId}
                                        label={`Abrir a assinatura ${a.identificador}`}
                                        onOpen={() => abrirVer(a)}
                                        acoes={podeGerenciar ? (
                                            <RowAction icon={<LuTrash2 size={15} />} label="Excluir assinatura" danger
                                                onClick={() => void excluir(a)} />
                                        ) : undefined}
                                    />
                                ))}
                        </ListCards>
                        {!isLoading && filtrados.length === 0 && listaVazia}
                        {!isLoading && filtrados.length > 0 && rodape}
                    </>
                ) : (
                    <div className="lk-table">
                        <div className="lk-scroll">
                            <div className="lk-row" style={{ gridTemplateColumns: COLUNAS }}>
                                <div className="lk-head">Identificador</div>
                                <div className="lk-head">Nome</div>
                                <div className="lk-head">Plano</div>
                                <div className="lk-head">Situação</div>
                                <div className="lk-head">Vencimento</div>
                                <div className="lk-head">Vínculos</div>
                                <div className="lk-head lk-head-acts">Ações</div>
                            </div>

                            <div className="lk-body">
                                {isLoading
                                    ? Array.from({ length: 8 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                    : visiveis.map(a => {
                                        const p = prazo(a);
                                        return (
                                            <ListRow
                                                key={a.assinaturaClienteId}
                                                columns={COLUNAS}
                                                selected={aberta?.assinaturaClienteId === a.assinaturaClienteId}
                                            >
                                                <span className="lk-cell lk-strong">
                                                    <span className="asg-identificador">{a.identificador}</span>
                                                </span>

                                                <span className={`lk-cell ${a.nome ? '' : 'lk-empty-value'}`}>
                                                    <span>{a.nome || 'sem nome'}</span>
                                                </span>

                                                <span className={`lk-cell ${a.produtoNome ? 'lk-muted' : 'lk-empty-value'}`}>
                                                    <span>{a.produtoNome || 'sem plano'}</span>
                                                </span>

                                                <span className="lk-cell">
                                                    <span className={`asg-selo asg-selo-${a.situacao}`}>
                                                        {ROTULO_SITUACAO[a.situacao] ?? '—'}
                                                    </span>
                                                </span>

                                                <span className={`lk-cell ${p.tom}`}><span>{p.texto}</span></span>

                                                <span className="lk-cell">
                                                    <span className="asg-vinculos">
                                                        {a.contatoNome && (
                                                            <span title={`Contato: ${a.contatoNome}`}><LuUser size={14} /></span>
                                                        )}
                                                        {a.negocioNome && (
                                                            <span title={`Negócio: ${a.negocioNome}`}><LuHandshake size={14} /></span>
                                                        )}
                                                        {!a.contatoNome && !a.negocioNome && (
                                                            <span className="lk-empty-value">—</span>
                                                        )}
                                                    </span>
                                                </span>

                                                {/* Olho + lixeira: quem abre o registro é o olho, não a linha. */}
                                                <RowActions>
                                                    <RowAction icon={<LuEye size={15} />} label="Ver assinatura"
                                                        onClick={() => abrirVer(a)} />
                                                    {podeGerenciar && (
                                                        <RowAction icon={<LuTrash2 size={15} />} label="Excluir assinatura"
                                                            danger onClick={() => void excluir(a)} />
                                                    )}
                                                </RowActions>
                                            </ListRow>
                                        );
                                    })}
                            </div>
                        </div>

                        {!isLoading && filtrados.length === 0 && listaVazia}
                        {!isLoading && filtrados.length > 0 && rodape}
                    </div>
                )}
            </div>

            {aberta && (
                <RecordPanel
                    open
                    wide
                    onClose={fechar}
                    title={rascunho.assinaturaClienteId ? aberta.identificador : 'Nova assinatura'}
                    subtitle={rascunho.assinaturaClienteId ? (aberta.nome || 'Sem nome') : undefined}
                    actions={podeGerenciar && modo === 'ver' && (
                        <button type="button" className="lk-btn" onClick={() => setModo('editar')}>
                            <LuPencil size={14} /> Editar
                        </button>
                    )}
                >
                    {modo === 'editar' ? (
                        <PanelSection title="Dados da assinatura">
                            <div className="asg-form">
                                <label>
                                    <span>Identificador *</span>
                                    <input
                                        type="text"
                                        value={rascunho.identificador}
                                        onChange={e => setRascunho({ ...rascunho, identificador: e.target.value })}
                                        placeholder="O login do assinante no seu serviço"
                                    />
                                </label>
                                <label>
                                    <span>Nome</span>
                                    <input
                                        type="text"
                                        value={rascunho.nome}
                                        onChange={e => setRascunho({ ...rascunho, nome: e.target.value })}
                                    />
                                </label>
                                <label>
                                    <span>Plano</span>
                                    <Select
                                        options={opcoesProduto}
                                        value={rascunho.produtoId}
                                        onChange={escolherProduto}
                                        placeholder={produtos.length ? 'Escolha um produto' : 'Nenhum produto cadastrado'}
                                        disabled={produtos.length === 0}
                                        searchFrom={8}
                                    />
                                </label>
                                <label>
                                    <span>Situação</span>
                                    {/* O mesmo Select do Plano: o nativo ao lado dele destoava
                                        na mesma linha do formulário. */}
                                    <Select
                                        options={[SITUACAO.ativa, SITUACAO.expirada, SITUACAO.cancelada]
                                            .map(v => ({ value: String(v), label: ROTULO_SITUACAO[v] }))}
                                        value={String(rascunho.situacao)}
                                        onChange={v => setRascunho({ ...rascunho, situacao: Number(v) })}
                                    />
                                </label>
                                <label>
                                    <span>Vence em</span>
                                    <input
                                        type="date"
                                        value={rascunho.expiraEm}
                                        onChange={e => setRascunho({ ...rascunho, expiraEm: e.target.value })}
                                    />
                                </label>
                                <label>
                                    <span>Entrou em</span>
                                    <input
                                        type="date"
                                        value={rascunho.aderiuEm}
                                        onChange={e => setRascunho({ ...rascunho, aderiuEm: e.target.value })}
                                    />
                                </label>
                                <label>
                                    <span>Valor</span>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={rascunho.valor}
                                        onChange={e => setRascunho({ ...rascunho, valor: e.target.value })}
                                    />
                                </label>
                                <label>
                                    <span>Quantidade</span>
                                    <input
                                        type="number"
                                        min="1"
                                        value={rascunho.quantidade}
                                        onChange={e => setRascunho({ ...rascunho, quantidade: e.target.value })}
                                    />
                                </label>
                                <label>
                                    <span>Origem</span>
                                    <input
                                        type="text"
                                        value={rascunho.origem}
                                        onChange={e => setRascunho({ ...rascunho, origem: e.target.value })}
                                        placeholder="Revenda, canal, parceiro"
                                    />
                                </label>
                                <label className="asg-form-largo">
                                    <span>Observação</span>
                                    <textarea
                                        rows={3}
                                        value={rascunho.observacao}
                                        onChange={e => setRascunho({ ...rascunho, observacao: e.target.value })}
                                    />
                                </label>
                            </div>
                            <div className="asg-form-acoes">
                                <button
                                    type="button"
                                    className="lk-btn"
                                    onClick={() => {
                                        if (rascunho.assinaturaClienteId) {
                                            setModo('ver');
                                            setRascunho(paraRascunho(aberta));
                                        } else {
                                            fechar();
                                        }
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="button"
                                    className="lk-btn is-primary"
                                    disabled={salvando}
                                    onClick={() => void salvar()}
                                >
                                    {salvando ? 'Salvando…' : 'Salvar'}
                                </button>
                            </div>
                        </PanelSection>
                    ) : (
                        <>
                            <PanelSection title="Dados da assinatura">
                                <dl className="asg-dados">
                                    <div><dt>Identificador</dt><dd>{aberta.identificador}</dd></div>
                                    <div><dt>Nome</dt><dd>{aberta.nome || '—'}</dd></div>
                                    <div><dt>Plano</dt><dd>{aberta.produtoNome || '—'}</dd></div>
                                    <div>
                                        <dt>Situação</dt>
                                        <dd>
                                            <span className={`asg-selo asg-selo-${aberta.situacao}`}>
                                                {ROTULO_SITUACAO[aberta.situacao]}
                                            </span>
                                        </dd>
                                    </div>
                                    <div><dt>Vence em</dt><dd>{prazo(aberta).texto}</dd></div>
                                    <div><dt>Entrou em</dt><dd>{formatarData(aberta.aderiuEm)}</dd></div>
                                    <div><dt>Último acesso</dt><dd>{formatarData(aberta.ultimoAcessoEm)}</dd></div>
                                    <div><dt>Valor</dt><dd>{moeda(aberta.valor)}</dd></div>
                                    <div><dt>Quantidade</dt><dd>{aberta.quantidade}</dd></div>
                                    <div><dt>Origem</dt><dd>{aberta.origem || '—'}</dd></div>
                                    {aberta.observacao && (
                                        <div className="asg-dados-largo">
                                            <dt>Observação</dt><dd>{aberta.observacao}</dd>
                                        </div>
                                    )}
                                </dl>
                            </PanelSection>

                            <PanelSection title="Vínculos">
                                {/* Vinculado: o cartão mostra quem é a pessoa e como falar com ela.
                                    Só o nome obrigaria a abrir Contatos para descobrir o telefone —
                                    que é justamente o motivo de vincular. */}
                                {aberta.contatoId ? (
                                    <div className="asg-cartao">
                                        <span className="lk-ava" aria-hidden="true">{iniciais(aberta.contatoNome)}</span>
                                        <div className="asg-cartao-corpo">
                                            <div className="asg-cartao-titulo">
                                                {aberta.contatoNome || 'Sem nome'}
                                                <span className="asg-cartao-tipo"><LuUser size={12} /> Contato</span>
                                            </div>
                                            <div className="asg-cartao-linhas">
                                                <span className={aberta.contatoTelefone ? 'lk-wa' : 'lk-empty-value'}>
                                                    {aberta.contatoTelefone ? <><i />{aberta.contatoTelefone}</> : 'sem telefone'}
                                                </span>
                                                <span className={aberta.contatoEmail ? 'lk-muted' : 'lk-empty-value'}>
                                                    <LuMail size={13} /> {aberta.contatoEmail || 'sem e-mail'}
                                                </span>
                                            </div>
                                            <div className="asg-cartao-acoes">
                                                {podeGerenciar && (
                                                    <>
                                                        <button type="button" className="lk-btn" onClick={() => void abrirVinculo('contato')}>
                                                            <LuLink size={14} /> Trocar
                                                        </button>
                                                        <button type="button" className="lk-btn" onClick={() => void gravarVinculo('contato', null)}>
                                                            <LuUnlink size={14} /> Desvincular
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="asg-vinculo-linha">
                                        <div className="asg-vinculo-rotulo"><LuUser size={14} /> Contato</div>
                                        <div className="asg-vinculo-valor">
                                            <span className="lk-empty-value">Sem contato vinculado</span>
                                        </div>
                                        {podeGerenciar && (
                                            <div className="asg-vinculo-acoes">
                                                <button type="button" className="lk-btn" onClick={() => void abrirVinculo('contato')}>
                                                    <LuLink size={14} /> Vincular
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {aberta.conversationId ? (
                                    <div className="asg-cartao">
                                        <span className="lk-ava" aria-hidden="true">{iniciais(aberta.negocioNome)}</span>
                                        <div className="asg-cartao-corpo">
                                            <div className="asg-cartao-titulo">
                                                {aberta.negocioNome || 'Sem nome'}
                                                <span className="asg-cartao-tipo"><LuHandshake size={12} /> Negócio</span>
                                            </div>
                                            <div className="asg-cartao-linhas">
                                                <span className="lk-muted">{nomeEtapa(aberta.negocioStatusId ?? 0)}</span>
                                                <span className={aberta.negocioValor ? 'lk-muted' : 'lk-empty-value'}>
                                                    {aberta.negocioValor ? moeda(aberta.negocioValor) : 'sem valor'}
                                                </span>
                                            </div>
                                            <div className="asg-cartao-acoes">
                                                <Link to={`/pipeline/deal/${aberta.conversationId}`} className="lk-btn">
                                                    <LuExternalLink size={14} /> Abrir negócio
                                                </Link>
                                                {podeGerenciar && (
                                                    <>
                                                        <button type="button" className="lk-btn" onClick={() => void abrirVinculo('negocio')}>
                                                            <LuLink size={14} /> Trocar
                                                        </button>
                                                        <button type="button" className="lk-btn" onClick={() => void gravarVinculo('negocio', null)}>
                                                            <LuUnlink size={14} /> Desvincular
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="asg-vinculo-linha">
                                        <div className="asg-vinculo-rotulo"><LuHandshake size={14} /> Negócio</div>
                                        <div className="asg-vinculo-valor">
                                            <span className="lk-empty-value">Sem negócio vinculado</span>
                                        </div>
                                        {podeGerenciar && (
                                            <div className="asg-vinculo-acoes">
                                                <button type="button" className="lk-btn" onClick={() => void abrirVinculo('negocio')}>
                                                    <LuLink size={14} /> Vincular
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {vinculando && (
                                    <div className="asg-seletor">
                                        <label className="lk-search">
                                            <LuSearch size={14} />
                                            <input
                                                type="search"
                                                autoFocus
                                                value={buscaVinculo}
                                                onChange={e => setBuscaVinculo(e.target.value)}
                                                placeholder={vinculando === 'contato' ? 'Buscar contato…' : 'Buscar negócio…'}
                                            />
                                        </label>
                                        <ul className="asg-opcoes">
                                            {opcoesVinculo.length === 0 ? (
                                                <li className="lk-empty-value">Nada encontrado.</li>
                                            ) : opcoesVinculo.map(o => (
                                                <li key={o.id}>
                                                    <button type="button" onClick={() => void gravarVinculo(vinculando, o.id)}>
                                                        <strong>{o.titulo}</strong>
                                                        {o.apoio && <span>{o.apoio}</span>}
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                        <button type="button" className="lk-btn" onClick={() => setVinculando(null)}>
                                            Cancelar
                                        </button>
                                    </div>
                                )}
                            </PanelSection>
                        </>
                    )}
                </RecordPanel>
            )}
        </div>
    );
};

export default AssinaturasPage;
